import React, { useEffect, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { NavBarContent } from 'feature/nav/NavBar';
import ReportTabs from '../Tabs';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { get, set, size, first } from 'global/lib/lodash';
import realmChange from 'global/wrappers/realmChange';
import PermissionGate from 'global/components/permission-gate';
import { useUser } from 'global/hooks/useUser';
import { REALM_ADD_LISTENERS } from '../../nav/NavRealm';
import ReportsLockIcon from '@mui/icons-material/Lock';
import Tooltip from '@mui/material/Tooltip';
import { formatSubScore } from '../AssessmentScore/SubScore/SubScoreLauncher';
import { userHasPermissions } from 'redux/actions/userLogin';
import { isActionable } from '../AssessmentScore/utils';
import sendData from '../../../redux/actions/sendData';

const AssessmentIncludes = ['realm', 'realm.realms', 'realm.parent', 'scorecards', 'scorecards.edition', 'scorecards.edition.realm', 'authors', 'editors', 'viewers', 'assignees', 'media', 'lockedBy'];

export const PScoreIncludes = 'checklist,subScores,subScores.images,subScores.realm,subScores.tradeTypes';

const include = AssessmentIncludes.join(',');

function ReportAssessment({ match, isTemplate }) {
  const { me, userBuilderRealm } = useUser();
  const [state, setState] = useState({
    scores: {
      loading: true,
      data: {},
      status: null,
      update: () => 0,
    }
  })

  const assessment = useRetrieveData({ url: `/${isTemplate ? 'assessment-templates' : 'assessments'}/${match.params.id}` }, {
    build: ['assessments'],
    transformData: (d) => {
      const v = d[0];
      return { ...v, availableStatus: Object.keys(v.availableStatus || []).map(k => v.availableStatus[k]) }
    },
    params: { include },
  });
  const highlights = useRetrieveData(
    { url: '/report-sections' },
    {
      build: ['reportSections'],
      params: {
        include: 'superset,images,realm,origin,user,assessment,origin.superset,origin.images,progeny',
        filter: { assessment_id: match.params.id },
      },
    },
  );
  const tasks = useRetrieveData(
    { url: '/tasks' },
    {
      build: ['tasks'],
      params: {
        filter: { assessment: match.params.id },
        page: { size: 1 },
      },
    },
  );

  const scores = useRetrieveData({ url: '/processed-scorecard-score-values' },
    {
      build: ['processedScorecardScoreValues'],
      params: {
        filter: { assessment_id: match.params.id },
        include: PScoreIncludes,
      },
    },
  );
  const targetscores = useRetrieveData({ url: '/processed-scorecard-score-values' },
    {
      build: ['processedScorecardScoreValues'],
      params: {
        filter: { assessment_id: match.params.id, status: 'target' },
      },
    }
  );
  const goalscores = useRetrieveData({ url: '/processed-scorecard-score-values' },
    {
      build: ['processedScorecardScoreValues'],
      params: {
        filter: { assessment_id: match.params.id, status: 'goal' },
      },
    }
  );
  const filterscores = useRetrieveData({ url: '/processed-scorecard-score-values' },
    {
      build: ['processedScorecardScoreValues'],
      waitFor: state.scores.status,
      setLoadingEvenIfWait: true,
      params: {
        filter: { assessment_id: match.params.id, status: state.scores.status },
      },
  }, [state.scores.status]);

  useEffect(() => {
    if (!scores.loading && !filterscores.loading && !targetscores.loading && !goalscores.loading) {
      const scdata = {};

      const getuid = i => {
        if (i.depth === 2 && !!i.scorecardScoreId) {
          // don't match to general observations
          return;
        }
        return `${i.scorecardId}_${i.systemId}_${i.supersetId}_${i.checklistId}_${i.checkpointId}`;
      }

      const isTarget = state.scores.status == 'target';

      scores.data.map((di) => {
        if (size(di.subScores)) {
          //note subscores is used instead of subScores, and this should hold true throughout
          //the rest of the site. Since we can't overwrite subScores since it doens't have a setter
          di.subscores = di.subScores.map((s) => formatSubScore(s, di));
        }

        if (get(di, 'depth') === 3) { set(di, 'filters.act', isActionable(di)); }

        set(di, 'compare', (isTarget ? targetscores.data : filterscores.data).find(d => getuid(d) == getuid(di)));
        set(di, 'target', targetscores.data.find(d => getuid(d) == getuid(di)));
        set(di, 'goal', goalscores.data.find(d => getuid(d) == getuid(di)));
        set(scdata, di.scorecardId, [...get(scdata, di.scorecardId, []), di]);
      });

      setState(s => ({ 
        ...s, 
        scores: { 
          ...s.scores, 
          data: scdata, 
          loading: false,
          update: async (p = {}) => { 
            const updateAll = () => {
              return Promise.all([
                scores.update({ ...p, force: true }),
                filterscores.update({ ...p, force: true }),
                targetscores.update({ ...p, force: true }),
                goalscores.update({ ...p, force: true })
              ])
            }

            if (!p.compare) { 
              assessment.update({ loading: false });
              return updateAll({}); 
            }

            const [st, url] = get(p, 'compare.links.self', '').split('/v1/');
            const all = await sendData({ url }, { 
              normalize: { 
                build: ['processedScorecardScoreValues']
              },
              params: { include: 'parent,parent.parent,parent.parent.parent' } 
            });

            const uids = all.map(a => getuid(a));

            const sc = isTarget ? targetscores : filterscores;
            
            const found = sc.data.find(f => uids.includes(getuid(f)));

            let isupdating;
            const forceUpdateAll = () => {
              if (!isupdating) {
                isupdating = true;
                updateAll();
              }
            }

            if (found) {
              sc.setLoading(true);
              setTimeout(() => {
                sc.manuallySetData(sc.data.map(d => {
                  const item = all.find(a => getuid(a) == getuid(d));
                  if (item) { 
                    if (item.value === d.value) {
                      forceUpdateAll();
                    }
                    return item 
                  }
                  return d;
                }))
              }, 200);
            } else {
              updateAll();
            }
          }
        } 
      }));
    }
  }, [scores.loading, filterscores.loading, targetscores.loading, goalscores.loading, filterscores.data, targetscores.data, goalscores.data, state.scores.status])

  const updateStatusFilter = (stat) => {
    setState(s => { 
      const status = stat == 'targetGoal' ? 'goal' : stat == 'active' ? null : stat;

      let loading = false;
      if (s.scores.status != status) {
        loading = true;
        filterscores.update();
        scores.update();
      }

      filterscores.setLoading(loading);

      return {
        ...s, 
        scores: { 
          ...s.scores, 
          loading,
          status,
        } 
      }
    })
  }

  const { data, loading } = assessment;

  const userIsEditor = (() => {
    if (loading) {
      return false;
    }

    const { editors, authors } = data;
    let isAdmin = false;
    if (userHasPermissions(['manageAccount'])) {
      isAdmin = userBuilderRealm.id == get(data, 'vendorId') && userBuilderRealm.id == get(data, 'clientId');
    }
    const isEditor = (editors || []).find((e) => e.id == me.id);
    const isAuthor = (authors || []).find((e) => e.id == me.id);
    return isAdmin || isEditor || isAuthor;
  })();

  const userCanEdit = (() => {
    if (loading) {
      return false;
    }
    if (get(data, 'locked') && !get(data, 'lockedBy', []).find((u) => u.id == me.id)) {
      return false;
    }
    return userIsEditor;
  })();

  const status = get(assessment, 'data.status', 'draft');

  const canShowScores = (() => {
    if (userIsEditor) {
      return true;
    }
    if (status === 'review') {
      return get(assessment, 'data.viewers', []).find((v) => v.id == me.id) || get(assessment, 'data.assignees', []).find((v) => v.id == me.id);
    } else if (status === 'published') {
      return true;
    } else {
      return false;
    }
  })();

  const userCanModify = userCanEdit && status !== 'published';

  useEffect(() => {
    //need to update the assessment if assessments were added from the realm picker
    window[REALM_ADD_LISTENERS].assessment = async () => {
      await assessment.update();
    };

    return () => {
      delete window[REALM_ADD_LISTENERS].assessment;
    };
  }, []);

  return (
    <>
      {data && (
        <NavBarContent
          space={true}
          pageTitle={
            get(data, 'locked') ? (
              <>
                <Tooltip title={get(data, 'locked') ? 'Locked by: ' + get(data, 'lockedBy.0.name') : ''}
                         placement="left">
                  <ReportsLockIcon style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                </Tooltip>
                {data.name}
              </>
            ) : (
              data.name
            )
          }
          link="reports/list"
        />
      )}
      <Switch>
        <Route
          exact
          path="/reports/assessment/:id"
          render={(props) => (
            <ReportTabs page={match.url} assessment={assessment} scores={state.scores} highlights={highlights} tasks={tasks}
                        userCanEdit={userCanEdit} canShowScores={canShowScores} currentTabValue="info" />
          )}
        />
        <Route
          exact
          path="/reports/assessment-template/:id"
          render={(props) => (
            <ReportTabs isTemplate page={match.url} assessment={assessment} scores={state.scores} highlights={highlights} tasks={tasks}
                        userCanEdit={userCanEdit} canShowScores={canShowScores} currentTabValue="info" />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/info"
          render={(props) => (
            <ReportTabs page={match.url} assessment={assessment} scores={state.scores} highlights={highlights} tasks={tasks}
                        userCanEdit={userCanEdit} canShowScores={canShowScores} currentTabValue="info" />
          )}
        />
        <Route
          exact
          path="/reports/assessment-template/:id/info"
          render={(props) => (
            <ReportTabs isTemplate page={match.url} assessment={assessment} scores={state.scores} highlights={highlights} tasks={tasks}
                        userCanEdit={userCanEdit} canShowScores={canShowScores} currentTabValue="info" />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/scores"
          render={(props) => (
            <ReportTabs
              isTemplate={isTemplate}
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights} tasks={tasks}
              userCanEdit={userCanEdit}
              userCanModify={userCanModify}
              canShowScores={canShowScores}
              updateStatusFilter={updateStatusFilter}
              currentTabValue="scores"
            />
          )}
        />
        <Route
          exact
          path="/reports/assessment-template/:id/scores"
          render={(props) => (
            <ReportTabs
              isTemplate={isTemplate}
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights} tasks={tasks}
              userCanEdit={userCanEdit}
              userCanModify={userCanModify}
              canShowScores={canShowScores}
              updateStatusFilter={updateStatusFilter}
              currentTabValue="scores"
            />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/highlights"
          render={(props) => (
            <ReportTabs page={match.url} assessment={assessment} scores={state.scores} highlights={highlights} tasks={tasks}
                        userCanEdit={userCanEdit} canShowScores={canShowScores} currentTabValue="highlights" />
          )}
        />
        <Route
          exact
          path="/reports/assessment-template/:id/highlights"
          render={(props) => (
            <ReportTabs isTemplate page={match.url} assessment={assessment} scores={state.scores} highlights={highlights} tasks={tasks}
                        userCanEdit={userCanEdit} canShowScores={canShowScores} currentTabValue="highlights" />
          )}
        />
        <Route
          exact
          path="/reports/assessment/:id/photos"
          render={(props) => (
            <ReportTabs
              isTemplate={isTemplate}
              page={match.url}
              assessment={assessment}
              scores={state.scores}
              highlights={highlights} tasks={tasks}
              userCanEdit={userCanEdit}
              canShowScores={canShowScores}
              assessmentId={match.params.id}
              currentTabValue="photos"
            />
          )}
        />
      </Switch>
    </>
  );
}

export default PermissionGate(realmChange(ReportAssessment));
